"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Truck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const deliveryHighlights = [{
  title: "Service Region",
  desc: "Serving Southern California and our listed Las Vegas-area communities.",
  linkText: "",
  linkTo: ""
}, {
  title: "Delivery and Pickup",
  desc: "Delivery and pickup are scheduled and confirmed with each quote. Positioning, setup, site-access requirements, and 53-foot reefer delivery requirements are reviewed individually.",
  linkText: "",
  linkTo: ""
}, {
  title: "Trailer Options",
  desc: "Choose from 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus a 53-foot reefer trailer. Generator and shelving options may be available for eligible smaller trailers. Power, fuel, and optional-equipment requirements for the 53-foot reefer are confirmed individually.",
  linkText: "Explore 53-foot reefer trailer options",
  linkTo: "/FootRefrigeratedTrailerRentalsPage"
}];
export default function Coverage() {
  const shouldReduceMotion = useReducedMotion();
  const [selectedRegion, setSelectedRegion] = useState<"socal" | "vegas">("socal");
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const nextRegion = selectedRegion === "socal" ? "vegas" : "socal";
      setSelectedRegion(nextRegion);
      const targetId = nextRegion === "socal" ? "tab-southern-california" : "tab-las-vegas-area";
      document.getElementById(targetId)?.focus();
    }
  };
  return <section id="coverage-area" data-nav="light" className="relative bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Column: Information & Corridors */}
          <motion.div className="lg:col-span-7 flex flex-col justify-center" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeInVariants}>
            {/* Cold-chain status pill */}
            <div className="inline-flex items-center gap-2 self-start rounded-full bg-accent/20 px-3.5 py-1 border border-accent/40 text-xs font-semibold uppercase tracking-wider text-foreground mb-4">
              <span className="size-2 rounded-full bg-chart-1 animate-pulse" />
              Service Coverage
            </div>

            <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight mb-5">Service Areas</h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8">Mobile Cold Storage Service Areas</p>

            {/* Region Breakdown Line */}
            <p className="text-base font-semibold text-foreground mb-8">Fridge Fleet serves Southern California and the listed Las Vegas-area communities with mobile refrigerated and freezer trailer rental options. Coverage, trailer availability, delivery requirements, site access, and scheduling are confirmed individually based on trailer size, delivery location, and rental dates.</p>

            {/* Quick Dispatch Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              {deliveryHighlights.map((item, index) => <div key={item.title} data-index={index} className="rounded-md border border-border bg-muted/60 p-3.5">
                  <p className="text-xs font-semibold text-foreground mb-1">
                    {item.title}
                  </p>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {item.desc}
                  </p>
                  {item.linkTo && item.linkText ? <p className="mt-2">
                      <Link to={item.linkTo} className="text-xs font-medium text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                        {item.linkText}
                      </Link>
                    </p> : null}
                </div>)}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button asChild size="lg" className="font-semibold shadow-xs">
                <Link to="/ContactRequestAQuotePage">
                  Request a Quote
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <a href="tel:2135218821" className="inline-flex items-center justify-center rounded-md bg-secondary text-secondary-foreground text-sm font-semibold h-10 px-6">
                213-521-8821
              </a>
            </div>
          </motion.div>

          {/* Right Column: Embedded Map Card & Navigation */}
          <motion.div className="lg:col-span-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeInVariants}>
            <div className="rounded-lg border border-border bg-card p-6 shadow-md relative">
              {/* Header inside card */}
              <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                <div>
                  <h2 className="font-default text-lg font-bold text-card-foreground">
                    Service Coverage Overview
                  </h2>
                  <p className="text-xs text-muted-foreground">Mobile cold storage delivery, setup, and pickup</p>
                </div>
                <div className="flex items-center gap-1.5 rounded-full bg-accent/30 px-2.5 py-1 text-xs font-medium text-foreground">
                  <Truck className="size-3.5 text-chart-1" />
                  <span>Regional Service</span>
                </div>
              </div>

              {/* Interactive Service Area Map Group */}
              <div role="group" aria-label="Interactive service area map" className="relative">
                {/* Region Tablist */}
                <div role="tablist" aria-label="Select service region map" className="flex items-center gap-2 mb-3">
                  <Button id="tab-southern-california" role="tab" aria-selected={selectedRegion === "socal"} aria-controls="tabpanel-region-map" tabIndex={selectedRegion === "socal" ? 0 : -1} onClick={() => setSelectedRegion("socal")} onKeyDown={handleKeyDown} variant="ghost" className={`h-auto rounded-full px-2.5 py-1 text-xs transition-colors ${selectedRegion === "socal" ? "bg-accent/60 font-semibold text-foreground ring-1 ring-border shadow-2xs" : "bg-accent/30 font-medium text-foreground hover:bg-accent/50"}`}>
                    Southern California
                  </Button>
                  <Button id="tab-las-vegas-area" role="tab" aria-selected={selectedRegion === "vegas"} aria-controls="tabpanel-region-map" tabIndex={selectedRegion === "vegas" ? 0 : -1} onClick={() => setSelectedRegion("vegas")} onKeyDown={handleKeyDown} variant="ghost" className={`h-auto rounded-full px-2.5 py-1 text-xs transition-colors ${selectedRegion === "vegas" ? "bg-accent/60 font-semibold text-foreground ring-1 ring-border shadow-2xs" : "bg-accent/30 font-medium text-foreground hover:bg-accent/50"}`}>
                    Las Vegas Area
                  </Button>
                </div>

                {/* Tabpanel with region map */}
                <div role="tabpanel" id="tabpanel-region-map" aria-label={selectedRegion === "socal" ? "Fridge Fleet Southern California location" : "Fridge Fleet Las Vegas location"}>
                  <iframe src={selectedRegion === "socal" ? "https://www.google.com/maps?q=13726+Saticoy+Street,+Panorama+City,+California&z=10&output=embed" : "https://www.google.com/maps?q=7477+Ullom+Drive,+Las+Vegas,+Nevada&z=10&output=embed"} title={selectedRegion === "socal" ? "Fridge Fleet Southern California location" : "Fridge Fleet Las Vegas location"} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen className="w-full h-72 sm:h-80 rounded-md border border-border" />
                  <div className="mt-2">
                    {selectedRegion === "socal" ? <Link to="https://maps.google.com/?q=13726+Saticoy+Street+Panorama+City+California" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                        Open in Google Maps
                      </Link> : <Link to="https://maps.google.com/?q=7477+Ullom+Drive+Las+Vegas+Nevada" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                        Open in Google Maps
                      </Link>}
                  </div>
                </div>

                {/* Service Region Disclaimer */}
                <p className="mt-2 text-xs text-muted-foreground">
                  Map shows general service regions. Delivery coverage, trailer availability, and requirements are confirmed with each quote.
                </p>
              </div>
            </div>

            {/* Areas We Serve Navigation Block */}
            <div className="mt-5 rounded-lg border border-border bg-card p-5 shadow-xs">
              <h3 className="font-default text-base font-bold text-card-foreground mb-3">
                Areas We Serve
              </h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="#southern-california" className="text-sm font-medium text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                    Refrigerated trailer rentals in Southern California
                  </Link>
                </li>
                <li>
                  <Link to="#las-vegas-area" className="text-sm font-medium text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                    Refrigerated trailer rentals in the Las Vegas area
                  </Link>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}